import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, Shield } from 'lucide-react';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const Login = () => {
  const { login, completeTwoFactorLogin, loading, error, clearError, resetAuthState } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  const colors = useThemeColors();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    const result = await login(data.email, data.password);
    
    if (result.success) {
      if (result.requiresTwoFactor) {
        // Show 2FA modal
        setTwoFactorData({
          tempToken: result.tempToken,
          user: result.user
        });
        setShow2FAModal(true);
      } else {
        toast.success('Login successful!');
        navigate('/dashboard');
      }
    } else {
      toast.error(result.error || 'Login failed!');
    }
  };

  const handleTwoFactorSubmit = async () => {
    if (!twoFactorData || !verificationCode) {
      toast.error('Please enter the verification code');
      return;
    }

    setIsVerifying2FA(true);
    
    try {
      const result = await completeTwoFactorLogin(twoFactorData.tempToken, verificationCode);
      
      if (result.success) {
        toast.success('Login successful!');
        setShow2FAModal(false);
        setTwoFactorData(null);
        setVerificationCode('');
        navigate('/dashboard');
      } else {
        toast.error(result.error || '2FA verification failed!');
      }
    } catch (error) {
      toast.error('2FA verification failed. Please try again.');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-background py-12 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Login" 
        description="Sign in to your TripTrot account to manage parcel deliveries and track shipments"
        keywords={['login', 'sign in', 'account access', 'trip trot login']}
        canonical="/login"
        noindex={true}
      />
      <div className="max-w-md w-full space-y-8 bg-theme-card rounded-lg shadow p-6 border-theme-light border">
        <div className="text-center">
          <img 
            src="/logo.png" 
            alt="TripTrot Logo" 
            className="h-12 w-12 mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-theme-primary">Signin</h2>
          <p className="mt-2 text-theme-secondary">Welcome back! Please sign in to your account.</p>
        </div>
        
        <form className="mt-8 space-y-6 bg-theme-card rounded-lg shadow p-6 border-theme-light border" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-theme-secondary">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            {/* <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div> */}

            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}
          <div className="text-center">
            <span className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                Sign up
              </Link>
            </span>
          </div>
        </form>
      </div>

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-theme-primary bg-opacity-10 rounded-full mb-4">
                <Shield className="h-6 w-6 text-theme-primary" />
              </div>
              <h3 className="text-lg font-medium text-theme-primary text-center mb-2">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-theme-secondary text-center mb-6">
                Enter the 6-digit code from your authenticator app to complete login
              </p>
              
              <div className="space-y-4">
                <div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setVerificationCode(value);
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-theme-primary focus:border-theme-primary text-center text-lg tracking-widest ${
                        verificationCode.length === 6 
                          ? 'border-theme-success ring-2 ring-theme-success ring-opacity-50' 
                          : 'border-theme-light'
                      }`}
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                    {verificationCode.length === 6 && (
                      <div className="absolute inset-y-0 right-3 flex items-center">
                        <Shield className="h-5 w-5 text-theme-success" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-theme-secondary mt-2">
                    Enter the 6-digit code from your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      // Reset auth state and close modal
                      resetAuthState();
                      setShow2FAModal(false);
                      setTwoFactorData(null);
                      setVerificationCode('');
                      setIsVerifying2FA(false);
                    }}
                    className="flex-1 px-4 py-2 border border-theme-light text-theme-secondary rounded-md hover:bg-theme-neutral-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTwoFactorSubmit}
                    disabled={isVerifying2FA || verificationCode.length !== 6}
                    className="flex-1 px-4 py-2 bg-theme-primary text-theme-inverse rounded-md hover:bg-theme-primary-hover disabled:opacity-50"
                  >
                    {isVerifying2FA ? (
                      <span className="flex items-center justify-center">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Verifying...
                      </span>
                    ) : (
                      'Verify'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
